import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAdminSubscriptions,
  getAdminSubscription,
  terminateSubscription,
} from '@/lib/api/admin-subscriptions';
import type {
  ListAdminSubscriptionsParams,
  AdminSubscription,
  AdminSubscriptionListResponse,
} from '@/types/admin-subscription';

export const adminSubscriptionKeys = {
  all: ['admin-subscriptions'] as const,
  lists: () => [...adminSubscriptionKeys.all, 'list'] as const,
  list: (params?: ListAdminSubscriptionsParams) =>
    [...adminSubscriptionKeys.lists(), params] as const,
  details: () => [...adminSubscriptionKeys.all, 'detail'] as const,
  detail: (id: string) => [...adminSubscriptionKeys.details(), id] as const,
};

export function useAdminSubscriptions(params?: ListAdminSubscriptionsParams) {
  return useQuery({
    queryKey: adminSubscriptionKeys.list(params),
    queryFn: () => listAdminSubscriptions(params),
    staleTime: 30_000,
  });
}

export function useAdminSubscription(subscriptionId: string | undefined) {
  return useQuery({
    queryKey: adminSubscriptionKeys.detail(subscriptionId ?? ''),
    queryFn: () => getAdminSubscription(subscriptionId!),
    enabled: !!subscriptionId,
    staleTime: 30_000,
  });
}

export function useTerminateSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, reason }: { subscriptionId: string; reason?: string }) =>
      terminateSubscription(subscriptionId, reason),
    onMutate: async ({ subscriptionId }) => {
      await queryClient.cancelQueries({ queryKey: adminSubscriptionKeys.lists() });

      const previousLists = queryClient.getQueriesData<AdminSubscriptionListResponse>({
        queryKey: adminSubscriptionKeys.lists(),
      });

      // Optimistically update cached lists
      queryClient.setQueriesData<AdminSubscriptionListResponse>(
        { queryKey: adminSubscriptionKeys.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((sub) =>
              sub.subscriptionId === subscriptionId
                ? { ...sub, status: 'terminated' as const, terminatedAt: new Date().toISOString() }
                : sub
            ),
          };
        }
      );

      return { previousLists };
    },
    onSuccess: (updated: AdminSubscription) => {
      queryClient.setQueryData(
        adminSubscriptionKeys.detail(updated.subscriptionId),
        updated
      );
      // Delay list invalidation — Facade may still return 'active' briefly
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: adminSubscriptionKeys.lists() });
      }, 5_000);
    },
    onError: (_err, _vars, context) => {
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
  });
}
